import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AccountPicker,
  Button,
  ConnectedAnnotatedField,
  PrimaryFullScreenSpinner,
  Tabs,
  TabsList,
  TabsTrigger,
  Txt,
  toast,
  useAppForm,
} from "@repo/ui";
import { useStore } from "@tanstack/react-form";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Decimal from "decimal.js";
import { Suspense } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import type { BypassApprovalRequestReason } from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/payouts_/new")({
  head: () => opsHead("New Payout"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const scheduleValues = ["NOW", "LATER"] as const;
const payoutBypassApprovalOptionValues = [
  "DO_NOT_BYPASS_APPROVAL_POLICY",
  "OPERATOR_BYPASSED_POLICY",
] as const satisfies readonly BypassApprovalRequestReason[];

type PayoutBypassApprovalOption =
  (typeof payoutBypassApprovalOptionValues)[number];

const payoutBypassApprovalOptionLabels: Record<
  PayoutBypassApprovalOption,
  string
> = {
  DO_NOT_BYPASS_APPROVAL_POLICY: "Do not bypass approval policy",
  OPERATOR_BYPASSED_POLICY: "Bypass approval policy",
};

const payoutBypassApprovalOptions = payoutBypassApprovalOptionValues.map(
  (value) => ({
    value,
    label: payoutBypassApprovalOptionLabels[value],
  }),
);

const formSchema = z
  .object({
    organisationId: z.string().nonempty({ message: "Required" }),
    beneficiaryAccountId: z.string().nonempty({ message: "Required" }),
    bypassApprovalsOption: z.enum(payoutBypassApprovalOptionValues, {
      error: "Required",
    }),
    sourceAssetId: z.string().nonempty({ message: "Required" }),
    amount: z
      .custom<Decimal>(Decimal.isDecimal, {
        message: "Please enter an amount",
      })
      .refine((val) => val?.gt(1), "Amount must be greater than $1")
      .describe("Amount"),
    schedule: z.enum(scheduleValues),
    date: z.date().optional(),
    reference: z.string().optional(),
    suppressNotifications: z.boolean(),
  })
  .refine(
    (data) => {
      if (data.schedule === "LATER") {
        return data.date !== undefined;
      }
      return true;
    },
    {
      message: "Date is required",
      path: ["date"],
    },
  );

type FormData = z.infer<typeof formSchema>;

function View() {
  const navigate = useNavigate();

  const { data } = useSuspenseQuery(
    graphql(`
      query AdminNewPayoutForm {
        organisations {
          ...AdminNewPayoutFormOrg
        }
      }

      fragment AdminNewPayoutFormOrg on Organisation {
        id
        name
        payoutApprovalPolicy {
          id
          userReviewRequired
        }
        thirdPartyPaymentApprovalPolicy {
          id
          userReviewRequired
        }
        assets {
          id
          ... AssetPickerFields
        }
        beneficiaryAccounts(includeInactive: false) {
          id
          kind
          ... BeneficiaryAccountPickerFields
        }
      }
  `),
  );

  const [createPayoutMutation, { loading }] = useMutation(
    graphql(`
      mutation AdminNewPayoutForm_Create($input: CreateNewPayoutInput!) {
        createNewPayout(input: $input) {
          payout {
            id
            paymentReference
          }
        }
      }
    `),
  );

  if (!data.organisations) {
    throw new Error("expected organisations");
  }

  const form = useAppForm({
    defaultValues: {
      bypassApprovalsOption: "DO_NOT_BYPASS_APPROVAL_POLICY",
      schedule: "NOW",
      suppressNotifications: false,
    } as FormData,
    validators: {
      onSubmit: formSchema,
    },
    onSubmit: async ({ value }) => {
      const defaultErrorMessage =
        "There was an error submitting your form. Please try again later.";

      let date: Date | undefined;
      if (value.schedule === "LATER") {
        if (!value.date) {
          throw new Error("date required when schedule is LATER");
        }
        date = value.date;
      }

      await createPayoutMutation({
        variables: {
          input: {
            amount: value.amount,
            bypassApprovalPolicyOption: value.bypassApprovalsOption,
            date,
            reference: value.reference ?? "",
            beneficiaryAccountId: value.beneficiaryAccountId,
            organisationId: value.organisationId,
            sourceAssetId: value.sourceAssetId,
            suppressNotifications: value.suppressNotifications,
          },
        },
        onCompleted: (data) => {
          if (!data.createNewPayout?.payout.id) {
            toast({
              kind: "error",
              autoHide: false,
              title: "Failed to create payout",
              description: defaultErrorMessage,
            });
            throw new Error("failed to create payout");
          } else {
            toast({
              kind: "success",
              autoHide: true,
              title: "Created payout",
              description: `Created new payout ${data.createNewPayout.payout.paymentReference}`,
            });
            navigate({
              to: "/payouts/$payoutId",
              params: { payoutId: data.createNewPayout.payout.id },
            });
          }
        },
        onError: (error) => {
          const errorMessage =
            error.graphQLErrors?.[0]?.message || defaultErrorMessage;
          toast({
            kind: "error",
            autoHide: false,
            title: "Failed to create payout",
            description: errorMessage,
          });
          throw error;
        },
      });
    },
  });

  const organisationOptions = data.organisations
    .map((org) => ({
      value: org.id,
      label: org.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const selectedOrganisationId = useStore(
    form.store,
    (state) => state.values.organisationId,
  );
  const selectedOrganisation = data.organisations.find(
    (o) => o.id === selectedOrganisationId,
  );

  const selectedBeneficiaryAccountId = useStore(
    form.store,
    (state) => state.values.beneficiaryAccountId,
  );
  const selectedSchedule = useStore(
    form.store,
    (state) => state.values.schedule,
  );

  const beneficiaryAccounts = selectedOrganisation?.beneficiaryAccounts ?? [];

  const selectedBeneficiaryAccount = beneficiaryAccounts.find(
    (a) => a.id === selectedBeneficiaryAccountId,
  );

  const selectedBeneficiaryCurrency = selectedBeneficiaryAccount?.currency;
  const approvalRequired =
    selectedBeneficiaryAccount?.kind === "PAYEE"
      ? (selectedOrganisation?.thirdPartyPaymentApprovalPolicy
          ?.userReviewRequired ?? false)
      : (selectedOrganisation?.payoutApprovalPolicy?.userReviewRequired ??
        false);

  const sourceAssetOptions =
    selectedOrganisation?.assets?.filter((a) => {
      if (a.class === "EXTERNAL_BANK_ACCOUNT") {
        return false;
      }
      if (
        selectedBeneficiaryCurrency &&
        a.class !== "FX" &&
        a.currency !== selectedBeneficiaryCurrency
      ) {
        return false;
      }
      return true;
    }) ?? [];

  (selectedBeneficiaryCurrency
    ? selectedOrganisation?.assets?.filter(
        (a) => a.class === "FX" || a.currency === selectedBeneficiaryCurrency,
      )
    : selectedOrganisation?.assets) ?? [];

  return (
    <AdminPageContainer testId="admin-payouts-new">
      <AdminPageHeader title="New Payout" subtitle="Create a new payout" />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <div className="flex flex-col gap-8">
          <form.AppField
            name="organisationId"
            listeners={{
              onChange: () => {
                form.resetField("beneficiaryAccountId");
                form.resetField("sourceAssetId");
              },
            }}
          >
            {(field) => (
              <field.SelectField label="Organisation" placeholder="Select...">
                {organisationOptions.map((o) => (
                  <field.SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </field.SelectItem>
                ))}
              </field.SelectField>
            )}
          </form.AppField>

          <form.AppField name="sourceAssetId">
            {(field) => (
              <ConnectedAnnotatedField label="Source account">
                <AccountPicker
                  accounts={sourceAssetOptions}
                  value={field.state.value}
                  onChange={(id) => field.handleChange(id)}
                  disabled={!selectedOrganisationId}
                  placeholder={
                    !selectedOrganisationId
                      ? "Select organisation first"
                      : "Search for source account"
                  }
                  showAvailableBalance
                  defaultGroupBy="ENTITY"
                  allSelectable
                />
              </ConnectedAnnotatedField>
            )}
          </form.AppField>

          <form.AppField name="beneficiaryAccountId">
            {(field) => (
              <ConnectedAnnotatedField label="Beneficiary account">
                <AccountPicker
                  accounts={[]}
                  beneficiaryAccounts={beneficiaryAccounts}
                  value={field.state.value}
                  onChange={(id) => field.handleChange(id)}
                  disabled={!selectedOrganisationId}
                  placeholder={
                    !selectedOrganisationId
                      ? "Select organisation first"
                      : "Search for beneficiary account"
                  }
                  defaultGroupBy="ENTITY"
                  allSelectable
                />
              </ConnectedAnnotatedField>
            )}
          </form.AppField>

          <div>
            <form.AppField name="amount">
              {(field) => (
                <field.CurrencyAmountField
                  currency={selectedBeneficiaryAccount?.currency ?? "AUD"}
                  align="start"
                  label="Amount"
                  placeholder={"Amount"}
                />
              )}
            </form.AppField>
          </div>
          <form.AppField name="schedule">
            {(field) => (
              <ConnectedAnnotatedField label="Date">
                <Tabs
                  value={field.state.value}
                  onValueChange={(o) =>
                    field.handleChange(o as (typeof scheduleValues)[number])
                  }
                >
                  <TabsList size="sm">
                    <TabsTrigger className="w-full" value="NOW">
                      Now
                    </TabsTrigger>
                    <TabsTrigger className="w-full" value="LATER">
                      Later
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </ConnectedAnnotatedField>
            )}
          </form.AppField>
          {selectedSchedule === "LATER" && (
            <form.AppField name="date">
              {(field) => (
                <div className="flex justify-start">
                  <field.DatePickerField placeholder={"Pick a date"} />
                </div>
              )}
            </form.AppField>
          )}
          <form.AppField name="reference">
            {(field) => <field.TextareaField label="Reference" />}
          </form.AppField>

          <div className="flex flex-col gap-2">
            <form.AppField name="bypassApprovalsOption">
              {(field) => (
                <field.SelectField label="Bypass approvals?">
                  {payoutBypassApprovalOptions.map((option) => (
                    <field.SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>
            {selectedOrganisation ? (
              approvalRequired ? (
                <Txt variant="body" tone="danger">
                  This organisation currently requires approvals.
                </Txt>
              ) : (
                <Txt variant="description">
                  This organisation does not currently require approvals.
                </Txt>
              )
            ) : null}
          </div>

          <form.AppField name="suppressNotifications">
            {(field) => <field.SwitchField label="Do not send notifications" />}
          </form.AppField>

          <div className="flex justify-end">
            <Button variant="primary" type="submit" disabled={loading}>
              Create payout
            </Button>
          </div>
        </div>
      </form>
    </AdminPageContainer>
  );
}
